import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, MessageCircleQuestion, Sparkles, Send, EyeOff, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

const fmt = (iso) => new Date(iso).toLocaleDateString("ro-RO", { day: "numeric", month: "long", year: "numeric" });
const STATUS = {
  nouă: "bg-[var(--tint-pink)] text-[var(--accent-dark)]",
  publicată: "bg-[var(--tint-mint)] text-[var(--teal-dark)]",
  respinsă: "bg-[var(--tint-butter)] text-[var(--sun-dark)]",
};

const QuestionCard = ({ q, onChange, onRemove }) => {
  const [answer, setAnswer] = useState(q.answer || "");
  const [busy, setBusy] = useState("");

  const draft = async () => {
    setBusy("draft");
    try {
      const { data } = await api.post(`/admin/questions/${q.id}/draft`);
      setAnswer(data.draft);
      toast.success("Schiță gata — ajusteaz-o în vocea ta.");
    } catch (e) {
      toast.error(formatApiError(e, "Asistentul n-a putut schița"));
    } finally {
      setBusy("");
    }
  };

  const save = async (status) => {
    if (status === "publicată" && answer.trim().length < 10) {
      toast.error("Scrie un răspuns înainte să publici.");
      return;
    }
    setBusy(status || "save");
    try {
      await api.patch(`/admin/questions/${q.id}`, { answer, ...(status ? { status } : {}) });
      onChange({ ...q, answer, status: status || q.status });
      toast.success(status === "publicată" ? "Publicat în cutie." : status === "respinsă" ? "Ascunsă." : "Răspuns salvat.");
    } catch (e) {
      toast.error(formatApiError(e, "Nu am putut salva"));
    } finally {
      setBusy("");
    }
  };

  return (
    <li data-testid={`question-${q.id}`} className="rounded-2xl bg-[#2B1B13] p-6 ring-1 ring-[color-mix(in_srgb,var(--milk)_18%,transparent)]">
      <div className="flex flex-wrap items-center gap-2">
        <span data-testid={`question-status-${q.id}`} className={`rounded-full px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-[0.15em] ${STATUS[q.status] || ""}`}>{q.status}</span>
        <span className="text-xs text-[#F7F1E8]/68">{fmt(q.created_at)}{q.nickname ? ` · „${q.nickname}”` : " · anonim"}{q.wants_notification ? " · va fi anunțat(ă) pe e-mail când publici" : ""}</span>
        <button type="button" onClick={onRemove} data-testid={`question-delete-${q.id}`} aria-label="Șterge" className="ml-auto rounded-full p-2 text-[#F7F1E8]/58 hover:bg-[var(--tint-pink)] hover:text-[var(--accent-dark)]">
          <Trash2 className="h-4 w-4" strokeWidth={1.6} />
        </button>
      </div>
      <p className="mt-3 font-serif text-xl leading-snug text-[#F7F1E8]">„{q.question}”</p>
      <textarea
        data-testid={`question-answer-${q.id}`}
        value={answer}
        onChange={(e) => setAnswer(e.target.value)}
        rows={6}
        maxLength={8000}
        placeholder="Răspunsul tău, în vocea ta. Paragrafele se separă printr-un rând gol."
        className="mt-4 w-full rounded-xl border border-[color-mix(in_srgb,var(--milk)_18%,transparent)] bg-[#0D0907] px-4 py-3 text-sm leading-relaxed outline-none focus:border-[var(--teal)] focus:ring-2 focus:ring-[var(--tint-mint)]"
      />
      <div className="mt-3 flex flex-wrap gap-2">
        <button type="button" onClick={draft} disabled={!!busy} data-testid={`question-draft-${q.id}`} className="inline-flex items-center gap-1.5 rounded-full border border-[color-mix(in_srgb,var(--accent)_35%,transparent)] px-3.5 py-2 text-[11px] font-semibold uppercase tracking-[0.12em] text-[var(--accent-dark)] hover:bg-[var(--tint-pink)] disabled:opacity-50">
          {busy === "draft" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" strokeWidth={1.6} />} Schiță AI
        </button>
        <button type="button" onClick={() => save()} disabled={!!busy} data-testid={`question-save-${q.id}`} className="rounded-full border border-[color-mix(in_srgb,var(--milk)_20%,transparent)] px-3.5 py-2 text-[11px] font-semibold uppercase tracking-[0.12em] text-[var(--milk)] disabled:opacity-50">
          Salvează
        </button>
        {q.status !== "publicată" && (
          <button type="button" onClick={() => save("publicată")} disabled={!!busy} data-testid={`question-publish-${q.id}`} className="inline-flex items-center gap-1.5 rounded-full bg-[var(--teal)] px-3.5 py-2 text-[11px] font-semibold uppercase tracking-[0.12em] text-white disabled:opacity-50">
            <Send className="h-3.5 w-3.5" strokeWidth={1.8} /> Publică răspunsul
          </button>
        )}
        {q.status !== "respinsă" && (
          <button type="button" onClick={() => save("respinsă")} disabled={!!busy} data-testid={`question-hide-${q.id}`} className="inline-flex items-center gap-1.5 rounded-full px-3.5 py-2 text-[11px] font-semibold uppercase tracking-[0.12em] text-[#F7F1E8]/68 hover:bg-[var(--tint-butter)] disabled:opacity-50">
            <EyeOff className="h-3.5 w-3.5" strokeWidth={1.8} /> {q.status === "publicată" ? "Retrage" : "Nu răspund"}
          </button>
        )}
      </div>
    </li>
  );
};

export const AdminQuestionsPage = () => {
  const [items, setItems] = useState(null);

  useEffect(() => {
    api.get("/admin/questions").then((r) => setItems(r.data)).catch((e) => toast.error(formatApiError(e, "Nu am putut încărca întrebările")));
  }, []);

  const remove = async (id) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm("Ștergi întrebarea definitiv?")) return;
    try {
      await api.delete(`/admin/questions/${id}`);
      setItems((l) => l.filter((q) => q.id !== id));
    } catch (e) {
      toast.error(formatApiError(e, "Nu am putut șterge"));
    }
  };

  return (
    <main data-testid="admin-questions" className="mx-auto max-w-4xl px-6 py-12">
      <Link to="/admin" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#F7F1E8]/78 hover:text-[#F7F1E8]">
        <ArrowLeft className="h-3.5 w-3.5" strokeWidth={1.6} /> Înapoi la Atelier
      </Link>
      <div className="mt-6">
        <p className="text-xs uppercase tracking-[0.3em] text-[var(--teal-dark)]">Cutia cu întrebări anonime</p>
        <h1 className="mt-2 font-serif text-4xl font-light text-[#F7F1E8]">Întrebările primite</h1>
        <p className="mt-2 text-sm text-[#F7F1E8]/78">Alege la ce răspunzi. „Publică răspunsul” îl pune pe pagina publică; „Nu răspund” o ascunde fără s-o ștergi.</p>
      </div>
      {items === null ? (
        <p className="py-16 text-center text-sm text-[#F7F1E8]/68">Se încarcă…</p>
      ) : items.length === 0 ? (
        <div data-testid="questions-empty" className="mt-10 rounded-3xl bg-[var(--tint-mint)] p-12 text-center">
          <MessageCircleQuestion className="mx-auto h-8 w-8 text-[var(--teal-dark)]" strokeWidth={1.4} />
          <p className="mt-4 font-serif text-2xl text-[#F7F1E8]">Cutia e goală deocamdată.</p>
        </div>
      ) : (
        <ul className="mt-10 space-y-4" data-testid="questions-list">
          {items.map((q) => (
            <QuestionCard key={q.id} q={q} onChange={(nq) => setItems((l) => l.map((x) => (x.id === nq.id ? nq : x)))} onRemove={() => remove(q.id)} />
          ))}
        </ul>
      )}
    </main>
  );
};

export default AdminQuestionsPage;
